import { useState } from 'react';
import { useNavigate } from 'react-router';
import { CheckCircle2, Download, Eye, GitCompare, Loader2, Bell } from 'lucide-react';
import { toast } from 'sonner';
import {
  comparisonApi,
  doctorComparisonApi,
  samplesApi,
  getErrorMessage,
  type ComparisonOverview,
} from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ComparisonReconciliationCardProps {
  mode: 'admin' | 'doctor';
  sampleId?: string;
  assignmentId?: string;
  comparison: ComparisonOverview | null;
  sampleName?: string;
  sampleStatus?: string;
  reconciledAt?: string | null;
  onReload: () => void;
}

export function ComparisonReconciliationCard({
  mode,
  sampleId,
  assignmentId,
  comparison,
  sampleName,
  sampleStatus,
  reconciledAt,
  onReload,
}: ComparisonReconciliationCardProps) {
  const navigate = useNavigate();
  const [notifyingPatchId, setNotifyingPatchId] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [pendingExportFormat, setPendingExportFormat] = useState<'xml' | 'json' | 'npy' | null>(null);
  const [exporting, setExporting] = useState(false);

  const conflictPatches = comparison?.patches ?? [];
  const allResolved = conflictPatches.length === 0 || conflictPatches.every((p) => p.is_resolved);
  const isFinalized = Boolean(
    comparison?.reconciled_at || reconciledAt || sampleStatus === 'completed',
  );
  const showAdminNoConflictFinalize =
    mode === 'admin' && Boolean(comparison) && conflictPatches.length === 0 && !isFinalized;
  const canAdminDownloadNow = mode === 'admin' && isFinalized;

  const viewerPath = (patchId: string) =>
    mode === 'admin'
      ? `/admin/samples/${sampleId}/comparison/patches/${patchId}`
      : `/doctor/assignments/${assignmentId}/comparison/patches/${patchId}`;

  const handleNotifyDoctors = async (patchId: string) => {
    if (!sampleId) return;
    setNotifyingPatchId(patchId);
    try {
      await comparisonApi.notify(sampleId, patchId);
      toast.success('Assigned doctors notified about this conflict');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setNotifyingPatchId(null);
    }
  };

  const handleFinalize = async () => {
    if (mode === 'admin') {
      if (!sampleId) return;
      setFinalizing(true);
      try {
        await comparisonApi.finalize(sampleId);
        toast.success('Reconciliation finalized');
        onReload();
      } catch (err) {
        toast.error(getErrorMessage(err));
      } finally {
        setFinalizing(false);
      }
      return;
    }
    if (!assignmentId) return;
    setFinalizing(true);
    try {
      await doctorComparisonApi.finalize(assignmentId);
      toast.success('Reconciliation finalized — admin has been notified');
      onReload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setFinalizing(false);
    }
  };

  const handleExport = async (fmt: 'xml' | 'json' | 'npy', includeImages: boolean) => {
    if (!sampleId) return;
    setExporting(true);
    try {
      const { data } = await samplesApi.export(sampleId, fmt, includeImages);
      const baseName = comparison?.sample_name ?? sampleName ?? 'sample';
      // NPY is always a ZIP of per-image *_seg.npy (optionally with images).
      if (includeImages || fmt === 'npy') {
        const blob = data as Blob;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = includeImages
          ? `${baseName}_export.zip`
          : `${baseName}_seg_npy.zip`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (fmt === 'json') {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseName}_annotations.json`;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        const blob = data as Blob;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${baseName}_annotations.${fmt}`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setExporting(false);
      setExportDialogOpen(false);
      setPendingExportFormat(null);
    }
  };

  const openExportDialog = (fmt: 'xml' | 'json' | 'npy') => {
    setPendingExportFormat(fmt);
    setExportDialogOpen(true);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center flex-wrap gap-2">
          <CardTitle className="flex items-center gap-2">
            <GitCompare className="size-5" /> Comparison &amp; Reconciliation
          </CardTitle>
          {canAdminDownloadNow ? (
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openExportDialog('xml')}>
                <Download className="size-4 mr-1" /> XML
              </Button>
              <Button size="sm" variant="outline" onClick={() => openExportDialog('json')}>
                <Download className="size-4 mr-1" /> JSON
              </Button>
              <Button size="sm" variant="outline" onClick={() => openExportDialog('npy')}>
                <Download className="size-4 mr-1" /> NPY
              </Button>
            </div>
          ) : (mode === 'doctor' && !isFinalized) || showAdminNoConflictFinalize ? (
            <Button
              size="sm"
              onClick={handleFinalize}
              disabled={finalizing || (!showAdminNoConflictFinalize && !allResolved)}
            >
              {finalizing ? <Loader2 className="size-4 mr-1 animate-spin" /> : <CheckCircle2 className="size-4 mr-1" />}
              Finalize
            </Button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {isFinalized && (
          <p className="text-sm text-green-600 flex items-center gap-1">
            <CheckCircle2 className="size-4" />
            Reconciliation finalized — the unified annotation file is ready to download.
          </p>
        )}
        {isFinalized ? null : !comparison ? (
          <p className="text-sm text-gray-500">Loading comparison…</p>
        ) : conflictPatches.length === 0 ? (
          <>
            <p className="text-sm text-gray-500">
              {mode === 'admin'
                ? 'No disagreements between reviewers.'
                : 'No disagreements between reviewers — you can finalize this sample directly.'}
            </p>
            {mode === 'doctor' && !isFinalized && (
              <Button size="sm" onClick={handleFinalize} disabled={finalizing}>
                {finalizing ? <Loader2 className="size-4 mr-1 animate-spin" /> : <CheckCircle2 className="size-4 mr-1" />}
                Finalize
              </Button>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500">
              {mode === 'admin' ? (
                <>Reviewers: {comparison.doctors.join(', ')}. Notify doctors to reconcile conflicts together.</>
              ) : (
                <>Work with the other assigned doctors to reconcile the conflicting cell types below.</>
              )}
            </p>
            {conflictPatches.map((p) => (
              <div key={p.patch_id} className="flex flex-wrap items-center justify-between gap-3 border rounded-lg p-4">
                <div>
                  <p className="font-medium">{p.patch_identifier}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline">{p.conflicts} conflict{p.conflicts === 1 ? '' : 's'}</Badge>
                    {p.is_resolved ? (
                      <Badge className="bg-green-600">Resolved</Badge>
                    ) : (
                      <Badge variant="destructive">Unresolved</Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  {mode === 'admin' && !isFinalized && comparison.doctors.length > 1 && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleNotifyDoctors(p.patch_id)}
                      disabled={notifyingPatchId === p.patch_id}
                    >
                      {notifyingPatchId === p.patch_id ? (
                        <Loader2 className="size-4 mr-1 animate-spin" />
                      ) : (
                        <Bell className="size-4 mr-1" />
                      )}
                      Notify
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigate(viewerPath(p.patch_id))}
                  >
                    <Eye className="size-4 mr-1" />
                    {mode === 'admin' ? 'Review' : p.is_resolved ? 'Review' : 'Reconcile'}
                  </Button>
                </div>
              </div>
            ))}
          </>
        )}
      </CardContent>

      <AlertDialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Download {pendingExportFormat?.toUpperCase()} annotations</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingExportFormat === 'npy'
                ? 'NPY export is a ZIP of one *_seg.npy per image (same format as upload). Optionally include the raw patch images as flat pairs in the ZIP.'
                : 'Do you want to include raw unannotated patch images as well? If yes, all images for this sample will be bundled with the annotation file in a ZIP download.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={exporting}
              onClick={(e) => {
                e.preventDefault();
                if (pendingExportFormat) void handleExport(pendingExportFormat, false);
              }}
            >
              Annotations only
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={exporting || !pendingExportFormat}
              onClick={(e) => {
                e.preventDefault();
                if (pendingExportFormat) void handleExport(pendingExportFormat, true);
              }}
            >
              {exporting ? <Loader2 className="size-4 mr-1 animate-spin" /> : null}
              Include raw images (ZIP)
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
